import React, { useCallback, useContext, useMemo, useRef, type ReactNode } from 'react';
import { serviceUrls } from '../config/serviceUrls';
import { getAuthHeaders } from '../utils/apiHeaders';
import {
  dialectTutorThemeFallback,
  isSameThemeAsset,
  mergeThemeAssetUrls,
} from '../utils/tutorThemeAssets';
import {
  TutorThemeContext,
  type TutorTheme,
  type TutorThemeFetchResult,
  type TutorThemeContextType,
} from './tutorThemeContext.shared';

export type {
  TutorTheme,
  TutorThemeFetchResult,
  TutorThemeFetchSource,
  TutorThemeContextType,
} from './tutorThemeContext.shared';

export const useTutorThemeCache = (): TutorThemeContextType => {
  const context = useContext(TutorThemeContext);
  if (!context) {
    throw new Error('useTutorThemeCache must be used within a TutorThemeCacheProvider');
  }
  return context;
};

function themeCacheKey(tenantId: string, dialectKey: string): string {
  return `${tenantId.trim()}:${dialectKey.trim()}`;
}

function resolveThemeMediaUrl(urlStr: string, gatewayBase: string): string {
  if (urlStr && urlStr.startsWith('/v1/')) {
    const base = gatewayBase.endsWith('/') ? gatewayBase.slice(0, -1) : gatewayBase;
    return `${base}${urlStr}`;
  }
  return urlStr;
}

interface ProviderProps {
  children: ReactNode;
}

export const TutorThemeCacheProvider: React.FC<ProviderProps> = ({ children }) => {
  const cacheRef = useRef<Record<string, TutorTheme>>({});
  const inflightRef = useRef<Map<string, Promise<TutorThemeFetchResult>>>(new Map());

  const getTheme = useCallback(async (tenantId: string, dialectKey: string): Promise<TutorThemeFetchResult> => {
    const tId = tenantId.trim();
    const dialect = dialectKey.trim();
    const key = themeCacheKey(tId, dialect);
    const dialectFallback = dialectTutorThemeFallback(dialect);

    const cached = cacheRef.current[key];
    if (cached) {
      return { theme: cached, source: 'cache' };
    }

    const existing = inflightRef.current.get(key);
    if (existing) return existing;

    const run = (async (): Promise<TutorThemeFetchResult> => {
      const AI_TUTOR_BASE = serviceUrls.gateway;
      const url = new URL('/v1/tutor/theme', AI_TUTOR_BASE);
      if (tId) url.searchParams.set('tenantId', tId);
      if (dialect) url.searchParams.set('dialect_key', dialect);

      if (import.meta.env.DEV) {
        console.info('[TutorTheme] GET', url.toString());
      }

      try {
        const res = await fetch(url.toString(), { headers: getAuthHeaders() });
        if (res.ok) {
          const data = (await res.json()) as {
            teacherImage?: string;
            backgroundImage?: string;
          };

          const resolvedTheme: TutorTheme = {
            teacherImage: resolveThemeMediaUrl(
              data.teacherImage || dialectFallback.teacherImage,
              AI_TUTOR_BASE,
            ),
            backgroundImage: resolveThemeMediaUrl(
              data.backgroundImage || dialectFallback.backgroundImage,
              AI_TUTOR_BASE,
            ),
          };

          const previous = cacheRef.current[key];
          const merged = previous
            ? mergeThemeAssetUrls(previous, resolvedTheme)
            : resolvedTheme;

          cacheRef.current[key] = merged;
          return { theme: merged, source: 'network' };
        }
      } catch (err) {
        console.warn('[TutorThemeCache] failed to load tutor theme, using fallback', err);
      }

      return { theme: dialectFallback, source: 'fallback' };
    })();

    inflightRef.current.set(key, run);
    try {
      return await run;
    } finally {
      if (inflightRef.current.get(key) === run) {
        inflightRef.current.delete(key);
      }
    }
  }, []);

  const value = useMemo(() => ({ getTheme }), [getTheme]);

  return <TutorThemeContext.Provider value={value}>{children}</TutorThemeContext.Provider>;
};

/** Exported for tests — compare signed GCS URLs by pathname identity. */
export function themeAssetsVisuallyEquivalent(previous: TutorTheme, incoming: TutorTheme): boolean {
  return (
    isSameThemeAsset(previous.teacherImage, incoming.teacherImage) &&
    isSameThemeAsset(previous.backgroundImage, incoming.backgroundImage)
  );
}
